import { normalizeProfileSystemId } from './vmkProductSpec';
import { BTW_MULTIPLIER_INCL } from './pdfPricing';

const round2 = (value) => Math.round(Number(value) * 100) / 100;

/** Automatic discount when VEKA (synego) profile is selected. */
export const VEKA_PROFILE_DISCOUNT_PERCENT = 4;

export const isVekaProfileSystem = (profileSystemId) =>
  normalizeProfileSystemId(profileSystemId) === 'synego';

export const calcVekaProfileDiscountAmount = (price, profileSystemId) => {
  if (!isVekaProfileSystem(profileSystemId) || !(Number(price) > 0)) return 0;
  return Math.round(Number(price) * (VEKA_PROFILE_DISCOUNT_PERCENT / 100) * 100) / 100;
};

export const applyVekaProfileDiscount = (price, profileSystemId) => {
  const discount = calcVekaProfileDiscountAmount(price, profileSystemId);
  return Number(price) - discount;
};

/** Apply admin pricing: markup margin, per-window costs, surcharge, then silent deduction. */
export function applyUnitPriceAdjustments(baseUnitPrice, pricingSettings, { isAuthenticated = false, parts = 1 } = {}) {
  if (!isAuthenticated || !pricingSettings) return baseUnitPrice;

  let price = baseUnitPrice;

  if (pricingSettings.margin_enabled && pricingSettings.margin_percentage > 0) {
    price *= 1 + pricingSettings.margin_percentage / 100;
  }

  if (pricingSettings.workers_fixing_enabled && (pricingSettings.workers_fixing_amount ?? 0) > 0) {
    price += Number(pricingSettings.workers_fixing_amount) || 0;
  }

  if (pricingSettings.material_cost_enabled && (pricingSettings.material_cost_amount ?? 0) > 0) {
    price += Number(pricingSettings.material_cost_amount) || 0;
  }

  if (pricingSettings.surcharge_enabled && pricingSettings.surcharge_amount > 0) {
    price += pricingSettings.surcharge_amount * (parts || 1);
  }

  if (pricingSettings.deduction_margin_enabled && pricingSettings.deduction_margin_percentage > 0) {
    const pct = Math.min(100, Math.max(0, Number(pricingSettings.deduction_margin_percentage) || 0));
    price *= 1 - pct / 100;
  }

  return price;
}

/** Silent percentage reduction applied after markup and per-window add-ons, before visible quote discount. */
export function applyDeductionMargin(price, pricingSettings, { isAuthenticated = false } = {}) {
  if (!isAuthenticated || !pricingSettings?.deduction_margin_enabled || !(pricingSettings.deduction_margin_percentage > 0)) {
    return price;
  }
  const pct = Math.min(100, Math.max(0, Number(pricingSettings.deduction_margin_percentage) || 0));
  return price * (1 - pct / 100);
}

/** Max EUR reduction a non-admin can apply on the export-quote screen (incl. BTW). */
export const MAX_EXPORT_DEDUCTION_EUR = 500;
export const MAX_USER_PRICE_REDUCTION_EUR = MAX_EXPORT_DEDUCTION_EUR;

export function clampExportDeductionEur(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(MAX_EXPORT_DEDUCTION_EUR, Math.round(n * 100) / 100);
}

/**
 * Clamp a price adjustment amount (incl. BTW, always >= 0).
 * - subtract + non-admin → max €500
 * - add → unlimited for everyone
 * - admin subtract → unlimited
 * Amounts are kept at 2 decimal places (cents).
 */
export function clampExportPriceAdjustmentEur(amount, mode = 'subtract', { isAdmin = false } = {}) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n < 0) return 0;
  const rounded = Math.round(n * 100) / 100;
  if (mode === 'subtract' && !isAdmin) {
    return Math.min(MAX_USER_PRICE_REDUCTION_EUR, rounded);
  }
  return rounded;
}

/** Parse user-entered EUR amounts; accepts 22.33, 22,33, .33, ,33 */
export function parseExportPriceAdjustmentAmount(raw) {
  if (raw === '' || raw == null) return 0;
  const normalized = String(raw).trim().replace(/\s/g, '').replace(',', '.');
  if (normalized === '' || normalized === '.' || normalized === '-') return 0;
  const parsed = parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** @deprecated Prefer applyExportPriceAdjustment — kept for older call sites. */
export function applyExportDeductionEur(totalExVat, enabled, amountExVat) {
  if (!enabled) return { total: totalExVat, deduction: 0 };
  const deduction = clampExportDeductionEur(amountExVat);
  return { total: Math.max(0, Number(totalExVat) - deduction), deduction };
}

/**
 * Normalize legacy deduction fields into add/subtract adjustment.
 * Stored fields:
 *   exportPriceAdjustmentEnabled, exportPriceAdjustmentAmount (>=0), exportPriceAdjustmentMode ('add'|'subtract')
 * Legacy:
 *   exportDeductionEnabled, exportDeductionAmount (always subtract)
 */
export function getExportPriceAdjustmentState(clientDetails) {
  if (!clientDetails) {
    return { enabled: false, amount: 0, mode: 'subtract' };
  }

  if (clientDetails.exportPriceAdjustmentEnabled === true) {
    const mode = clientDetails.exportPriceAdjustmentMode === 'add' ? 'add' : 'subtract';
    return {
      enabled: true,
      amount: Math.max(0, Number(clientDetails.exportPriceAdjustmentAmount) || 0),
      mode,
    };
  }

  // Legacy: deduction only
  if (clientDetails.exportDeductionEnabled === true) {
    return {
      enabled: true,
      amount: Math.max(0, Number(clientDetails.exportDeductionAmount) || 0),
      mode: 'subtract',
    };
  }

  return { enabled: false, amount: 0, mode: 'subtract' };
}

/** Signed adjustment incl. BTW: +add, −subtract, 0 if disabled. */
export function getQuotePriceAdjustmentInclVat(clientDetails) {
  const { enabled, amount, mode } = getExportPriceAdjustmentState(clientDetails);
  if (!enabled || amount <= 0) return 0;
  const value = round2(amount);
  return mode === 'add' ? value : -value;
}

/** Signed adjustment ex VAT. */
export function getQuotePriceAdjustmentExVat(clientDetails) {
  const incl = getQuotePriceAdjustmentInclVat(clientDetails);
  if (incl === 0) return 0;
  return round2(incl / BTW_MULTIPLIER_INCL);
}

/** Apply signed price adjustment to an ex-VAT total. */
export function applyExportPriceAdjustment(totalExVat, clientDetails) {
  const adj = getQuotePriceAdjustmentExVat(clientDetails);
  return Math.max(0, Number(totalExVat) + adj);
}

/**
 * Per-quote export deduction as entered — incl. BTW (positive when subtracting).
 * Kept for backward-compatible call sites; returns 0 for "add" mode.
 */
export function getQuoteExportDeductionInclVat(clientDetails) {
  const signed = getQuotePriceAdjustmentInclVat(clientDetails);
  return signed < 0 ? round2(Math.abs(signed)) : 0;
}

/** Ex-VAT portion of a reduction only (0 when adding). */
export function getQuoteExportDeductionExVat(clientDetails) {
  const incl = getQuoteExportDeductionInclVat(clientDetails);
  if (incl <= 0) return 0;
  return round2(incl / BTW_MULTIPLIER_INCL);
}

/**
 * Quote-level extra line items (Offerte Exporteren).
 * Amounts are entered/stored as incl. BTW; convert for ex-VAT subtotals.
 */
export function getQuoteExtraLineItemsInclVat(clientDetails) {
  const items = clientDetails?.extraLineItems;
  if (!Array.isArray(items) || items.length === 0) return 0;
  return round2(
    items.reduce((sum, item) => {
      const name = (item?.name || '').trim();
      const price = Number(item?.price) || 0;
      if (!name || price <= 0) return sum;
      return sum + price;
    }, 0),
  );
}

export function getQuoteExtraLineItemsExVat(clientDetails) {
  const incl = getQuoteExtraLineItemsInclVat(clientDetails);
  if (incl <= 0) return 0;
  return round2(incl / BTW_MULTIPLIER_INCL);
}
